class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.openFromUrl();
  }

  openFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('cart') !== 'open') return;

    url.searchParams.delete('cart');
    window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
    setTimeout(() => this.open());
  }

  setHeaderCartIconAccessibility() {
    document.querySelectorAll('#cart-icon-bubble, [data-cart-drawer-toggle]').forEach((cartLink) => {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasOpen = this.classList.contains('active');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Adding from a product page stays on the page; only the header icon reacts
      if (wasOpen) {
        this.open();
      } else {
        this.bumpCartIcon();
      }
    });
  }

  bumpCartIcon() {
    document.querySelectorAll('[data-cart-drawer-toggle]').forEach((cartLink) => {
      cartLink.classList.remove('cart-bump');
      void cartLink.offsetWidth;
      cartLink.classList.add('cart-bump');
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartUpsell extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('change', (event) => {
      const select = event.target.closest('[data-upsell-variant]');
      if (!select) return;
      // Keep the variant picker away from cart-drawer-items' quantity handler
      event.stopPropagation();

      const item = select.closest('[data-upsell-item]');
      const option = select.selectedOptions[0];
      item.querySelector('[data-upsell-price]').textContent = option.dataset.price;
      item.querySelector('[data-upsell-label]').textContent = option.textContent.trim();
      item.querySelector('[data-upsell-add]').dataset.variantId = select.value;
    });

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-upsell-add]');
      if (button) this.addToCart(button);
    });
  }

  addToCart(button) {
    if (button.classList.contains('loading')) return;
    button.classList.add('loading');
    button.setAttribute('aria-disabled', 'true');

    const cartDrawer = this.closest('cart-drawer');
    const config = fetchConfig('javascript');
    config.body = JSON.stringify({
      items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
      sections: cartDrawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}.js`, config)
      .then((response) => response.json())
      .then((state) => {
        if (state.status) throw new Error(state.description);
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(state);
        // 'cart-items' skips the drawer's own refetch; the header count still updates
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      })
      .catch((error) => console.error(error))
      .finally(() => {
        if (!button.isConnected) return;
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
      });
  }
}

customElements.define('cart-upsell', CartUpsell);

class CartDiscount extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-discount-apply]')) this.apply();

      const removeButton = event.target.closest('[data-discount-remove]');
      if (removeButton) {
        this.update(this.codes().filter((code) => code !== removeButton.dataset.discountRemove));
      }
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-discount-input]')) {
        event.preventDefault();
        this.apply();
      }
    });
  }

  codes() {
    return this.dataset.codes ? this.dataset.codes.split(',').filter(Boolean) : [];
  }

  apply() {
    const code = this.querySelector('[data-discount-input]').value.trim();
    if (!code) return;

    if (this.codes().some((applied) => applied.toLowerCase() === code.toLowerCase())) {
      this.showError('That code is already applied.');
      return;
    }

    this.update([...this.codes(), code], code);
  }

  async update(codes, newCode) {
    if (this.busy) return;
    this.busy = true;
    this.classList.add('loading');

    const cartDrawer = this.closest('cart-drawer');
    try {
      let state = await this.post(codes, cartDrawer);
      let error;

      if (newCode) {
        const match = (state.discount_codes || []).find(
          (discount) => discount.code.toLowerCase() === newCode.toLowerCase()
        );
        if (!match || !match.applicable) {
          error = `"${newCode}" isn't valid for the items in your cart.`;
          // Drop the rejected code so it doesn't linger on the cart
          state = await this.post(
            codes.filter((code) => code !== newCode),
            cartDrawer
          );
        }
      }

      cartDrawer.renderContents(state);
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      if (error) cartDrawer.querySelector('cart-discount')?.showError(error);
    } catch (error) {
      console.error(error);
      this.showError('Something went wrong. Please try again.');
    } finally {
      this.busy = false;
      this.classList.remove('loading');
    }
  }

  post(codes, cartDrawer) {
    const config = fetchConfig('javascript');
    config.body = JSON.stringify({
      discount: codes.join(','),
      sections: cartDrawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });
    return fetch(`${routes.cart_update_url}.js`, config).then((response) => response.json());
  }

  showError(message) {
    const details = this.querySelector('details');
    if (details) details.open = true;

    const errorElement = this.querySelector('[data-discount-error]');
    errorElement.textContent = message;
    errorElement.hidden = false;
  }
}

customElements.define('cart-discount', CartDiscount);
